<!-- @format -->

<template>
  <div>
    <!-- Experiment-driven hero (homepage_hero surface). Server-authored payload,
         so new variants need no deploy. Falls back to the static carousel below
         when no experiment is running. Exposure + assignment resolution stay in
         fetchHeroPayload; <Hero> is presentational. -->
    <Hero
      v-if="hero"
      class="mb-2"
      :title="hero.headline"
      :image="heroImage"
      :eyebrow="hero.eyebrow"
      :sub="hero.subhead"
      :cta="hero.cta"
      :link="hero.link"
    />
    <div v-else class="d-flex flex-row flex-wrap mb-2">
      <div id="homepageCarousel" class="carousel slide w-100">
        <button
          v-if="carouselCanPause"
          type="button"
          class="carousel-playpause"
          :aria-label="
            carouselPaused
              ? 'Play automatic slideshow'
              : 'Pause automatic slideshow'
          "
          :aria-pressed="carouselPaused ? 'true' : 'false'"
          @click="toggleCarouselPlayback"
        >
          <span aria-hidden="true">{{ carouselPaused ? "▶" : "⏸" }}</span>
        </button>
        <div class="carousel-indicators">
          <button
            type="button"
            data-bs-target="#homepageCarousel"
            data-bs-slide-to="0"
            class="active"
            aria-current="true"
            aria-label="Slide 1"
          ></button>
          <button
            type="button"
            data-bs-target="#homepageCarousel"
            data-bs-slide-to="1"
            aria-label="Slide 2"
          ></button>
        </div>
        <div class="carousel-inner">
          <div class="carousel-item active">
            <RouterLink
              to="/collection/california"
              style="color: inherit; text-decoration: none"
              title="Shop for framed art ready to hang"
            >
              <img
                :src="mediapath + 'static/banner-12.webp'"
                class="d-block w-100 carousel-image"
                alt="Framed fine-art photography ready to hang"
              />
              <div
                class="collection-banner collection-banner-top text-left col background-overlay"
              >
                <h1>Framed in real wood</h1>
                <ul class="white">
                  <li>Original archival prints</li>
                  <li>Real wood frames</li>
                  <!-- The FRAMED ladder: the live catalog's active framed_size
                       rows are 8x12 and 24x36, and Nations frames nothing
                       larger. Five sizes to 30x45 is the UNFRAMED ladder
                       (bead outstanding-cbik). -->
                  <li>Two sizes, 8x12 and 24x36</li>
                  <li>Made in 🇺🇸</li>
                </ul>
                <span
                  class="btn btn-primary mt-0"
                  style="
                    margin-left: 15px;
                    border: 2px white solid;
                    background-color: transparent;
                  "
                  >Shop Prints</span
                >
              </div>
            </RouterLink>
          </div>
          <div class="carousel-item">
            <RouterLink
              to="/collection/california"
              style="color: inherit; text-decoration: none"
              title="From across the golden state"
            >
              <img
                :src="mediapath + 'static/banner4.webp'"
                class="d-block w-100 carousel-image"
                alt="California landscape photography"
              />
              <div
                class="collection-banner collection-banner-bottom text-left col background-overlay"
              >
                <h1>California</h1>
                <h2>Across the golden state</h2>
              </div>
            </RouterLink>
          </div>
        </div>
        <button
          class="carousel-control-prev"
          type="button"
          data-bs-target="#homepageCarousel"
          data-bs-slide="prev"
        >
          <span class="carousel-control-prev-icon" aria-hidden="true"></span>
          <span class="visually-hidden">Previous</span>
        </button>
        <button
          class="carousel-control-next"
          type="button"
          data-bs-target="#homepageCarousel"
          data-bs-slide="next"
        >
          <span class="carousel-control-next-icon" aria-hidden="true"></span>
          <span class="visually-hidden">Next</span>
        </button>
      </div>
    </div>
    <div class="row">
      <div class="col-lg-4 col-md-4 col-sm-4 col-xs-12">
        <h2 class="promo-title text-center pt-3 pb-3">
          🖼️ Framed & Ready to Hang
        </h2>
        <h3 class="promo-text text-center pb-2">
          Ready to hang, no framing trip.
        </h3>
      </div>
      <div class="col-lg-4 col-md-4 col-sm-4 col-xs-12">
        <h2 class="promo-title text-center pt-3 pb-3">🪵 Real Wood Frames</h2>
        <p class="promo-text text-center pb-2">
          Handcrafted frames made in the USA from real wood
        </p>
      </div>
      <div class="col-lg-4 col-md-4 col-sm-4 col-xs-12">
        <h2 class="promo-title text-center pt-3 pb-3">
          🚚 Free Ground Shipping on orders {{ freeShippingThresholdText() }} or
          more
        </h2>
        <p class="promo-text text-center pb-2">
          Printed and dispatched within {{ PRODUCTION_WINDOW_DAYS }}
        </p>
      </div>
    </div>
    <!-- Cross-content row: destinations the collection grid below does NOT
         cover (stories, freshness, the framed pitch). Each card carries its
         own real image — no shared placeholder (bead: the old row repeated
         static/banner4.webp three times). -->
    <div class="row mb-3">
      <div class="col-lg-4 col-md-4 col-sm-6 col-xs-12 p-2">
        <div class="card">
          <RouterLink
            to="/stories"
            style="color: inherit; text-decoration: none"
            title="Stories from behind the lens"
          >
            <img
              src="https://api.outstandingphotos.shop/api/images/mic61mky-ca15fe2f/medium"
              class="card-img-top"
              alt="El Capitan with a snow-covered Half Dome behind — from the Yosemite story"
              loading="lazy"
              decoding="async"
            />
            <div
              class="card-img-overlay d-flex justify-content-center align-items-end"
            >
              <h5 class="card-title btn-text">Stories</h5>
            </div>
          </RouterLink>
        </div>
      </div>
      <div class="col-lg-4 col-md-4 col-sm-6 col-xs-12 p-2">
        <div class="card">
          <RouterLink
            to="/collection/whats-new"
            style="color: inherit; text-decoration: none"
            title="The latest photographs in the catalog"
          >
            <img
              src="https://api.outstandingphotos.shop/api/images/micdsjwd-72aae8eb/medium"
              class="card-img-top"
              alt="Retired rail cars along the Napa River at dusk — new in the catalog"
              loading="lazy"
              decoding="async"
            />
            <div
              class="card-img-overlay d-flex justify-content-center align-items-end"
            >
              <h5 class="card-title btn-text">What's New</h5>
            </div>
          </RouterLink>
        </div>
      </div>
      <div class="col-lg-4 col-md-4 col-sm-6 col-xs-12 p-2">
        <div class="card">
          <RouterLink
            to="/collection/california"
            style="color: inherit; text-decoration: none"
            title="Shop prints framed in real wood, ready to hang"
          >
            <img
              :src="mediapath + 'static/card-framed.webp'"
              class="card-img-top"
              alt="A framed print in a real wood frame, ready to hang"
              loading="lazy"
              decoding="async"
            />
            <div
              class="card-img-overlay d-flex justify-content-center align-items-end"
            >
              <h5 class="card-title btn-text">Framed</h5>
            </div>
          </RouterLink>
        </div>
      </div>
    </div>

    <div class="row gx-3 flex-wrap">
      <CollectionItem
        :collectionSlug="collection.slug"
        :imageUrl="collection.image_url ?? undefined"
        :title="collection.title"
        :class="['col-lg-4', 'col-md-4', 'col-sm-6', 'col-xs-12', 'mb-4']"
        v-for="collection in collections"
        v-bind:key="collection.id"
        :id="collection.id"
      ></CollectionItem>
    </div>
  </div>
</template>

<script lang="ts">
/** @format */

/**
 * Server-authored homepage_hero variant payload contract:
 *
 *   { banner, headline, eyebrow?, subhead?, cta?, link? }
 *
 * `banner` (image path or absolute http(s) URL) and `headline` are both
 * required — a hero without either renders visibly broken (empty img src /
 * blank overlay), so anything less falls back to the static carousel.
 * `eyebrow` is the small kicker label above the headline (e.g. a category);
 * optional so existing payloads render unchanged (the Hero omits it when blank).
 */
export interface HeroPayload {
  banner: string;
  headline: string;
  eyebrow?: string;
  subhead?: string;
  cta?: string;
  link?: string;
}

import { boundedString, isPlainObject } from "@/lib/experimentPayload";

// Defensive bounds so a mis-authored payload can't blow up the hero layout.
const HERO_TEXT_MAX_LEN = 300;
const HERO_LINK_MAX_LEN = 500;

/** Trimmed, length-capped string field, or undefined when absent/blank/not a string. */
/**
 * Validate + normalize a raw (unknown) homepage_hero variant payload — the
 * parsePromoBannerPayload pattern. Returns null unless it is an object with
 * non-blank string `headline` AND `banner`; a malformed payload renders the
 * static carousel instead of a broken hero. Exposure still fires for the
 * resolved assignment (see fetchHeroPayload) so the arms stay comparable.
 */
export function parseHeroPayload(raw: unknown): HeroPayload | null {
  if (!isPlainObject(raw)) return null;
  const headline = boundedString(raw.headline, HERO_TEXT_MAX_LEN);
  const banner = boundedString(raw.banner, HERO_LINK_MAX_LEN);
  if (!headline || !banner) return null;

  const payload: HeroPayload = { banner, headline };
  const eyebrow = boundedString(raw.eyebrow, HERO_TEXT_MAX_LEN);
  if (eyebrow) payload.eyebrow = eyebrow;
  const subhead = boundedString(raw.subhead, HERO_TEXT_MAX_LEN);
  if (subhead) payload.subhead = subhead;
  const cta = boundedString(raw.cta, HERO_TEXT_MAX_LEN);
  if (cta) payload.cta = cta;
  const link = boundedString(raw.link, HERO_LINK_MAX_LEN);
  if (link) payload.link = link;
  return payload;
}
</script>

<script setup lang="ts">
/** @format */

import { inject, onMounted, onUnmounted, ref } from "vue";
import carousel from "bootstrap/js/dist/carousel.js";
import CollectionItem from "./collection/CollectionItem.vue";
import Hero from "./Hero.vue";
import * as collectionApi from "@/services/api/collectionApi";
import type { Collection } from "@outstanding/shared";
// Production window + free-shipping threshold from the ONE SPA shipping-facts
// source (bead 49n.3b) so every surface states the same number and no copy
// hard-codes a value the server can change.
import {
  PRODUCTION_WINDOW_DAYS,
  freeShippingThresholdText,
} from "@/content/shippingFacts";
import { useExperimentSurface } from "@/composables/useExperimentSurface";
import { setDocumentHead } from "@/composables/useDocumentHead";

const mediapath = inject<string>("mediapath", "");
const collections = ref<Collection[]>([]);

// Experiment-driven hero (homepage_hero surface). Null → the static carousel in
// the template renders instead. Server-authored, so new variants need no deploy.
const hero = ref<HeroPayload | null>(null);

/**
 * The hero image src — absolute banner URLs pass through; bare paths are
 * resolved against the configured media path (same convention as the static
 * banners). Empty when there is no hero.
 */
function heroImageFor(h: HeroPayload | null): string {
  if (!h) return "";
  const banner = h.banner || "";
  return /^https?:\/\//.test(banner) ? banner : mediapath + banner;
}

const heroImage = ref<string>("");

// The live Bootstrap carousel instance, kept so it can be disposed on unmount.
// Without disposal each return to the homepage (the landing route) orphaned
// another carousel plus the detached DOM it retains, compounding on every visit.
// Bootstrap owns slide transitions + the prev/next/indicator controls; AUTOPLAY
// is driven by a Vue-managed timer below (not Bootstrap's `ride`) so the pause
// control can stop it definitively — otherwise Bootstrap re-cycles on mouse-leave
// whenever `ride` is set, defeating the pause.
let carouselInstance: {
  dispose?: () => void;
  nextWhenVisible?: () => void;
} | null = null;
let carouselTimer: ReturnType<typeof setInterval> | null = null;

const CAROUSEL_INTERVAL_MS = 4000;

// WCAG 2.2.2 (Pause, Stop, Hide): the static carousel auto-advances every 4s
// with no natural end, so users need a way to stop it. `carouselCanPause` is
// true only when autoplay is actually running (reduced-motion visitors get no
// auto-advance, so no control is shown); `carouselPaused` drives the toggle.
const carouselCanPause = ref(false);
const carouselPaused = ref(false);

function startCarouselAutoplay(): void {
  if (carouselTimer !== null) return;
  // nextWhenVisible() matches Bootstrap's own cycle: it skips advancing when the
  // tab is hidden / the carousel is off-screen.
  carouselTimer = setInterval(() => {
    carouselInstance?.nextWhenVisible?.();
  }, CAROUSEL_INTERVAL_MS);
  carouselPaused.value = false;
}

function stopCarouselAutoplay(): void {
  if (carouselTimer !== null) {
    clearInterval(carouselTimer);
    carouselTimer = null;
  }
  carouselPaused.value = true;
}

function toggleCarouselPlayback(): void {
  if (carouselPaused.value) startCarouselAutoplay();
  else stopCarouselAutoplay();
}

/**
 * Fetch the visitor's experiment assignments and resolve the homepage_hero
 * variant payload (or null). Records all assignments so later events + the
 * checkout order attribute to the right variant. Best-effort: any failure falls
 * back to the static hero.
 */
async function fetchHeroPayload(): Promise<HeroPayload | null> {
  try {
    // App.vue kicks the fetch off at app level (bead zpe.7); this awaits the
    // same memoized promise, so landing on the homepage costs one request.
    const match = await useExperimentSurface("homepage_hero");
    // Parse, never cast: a truthy-but-malformed payload used to render a
    // visibly broken hero (img src = mediapath + "", empty overlay). Anything
    // that fails the contract falls back to the static carousel.
    return parseHeroPayload(match?.payload);
  } catch {
    return null;
  }
}

// Static head — set once in setup. (Was in updated(), which fired on every
// re-render and stacked duplicate canonical/og tags.)
setDocumentHead({
  title: "Fine Art Photography Prints",
  description:
    "My collection of fine art photography from around the world, available in a range of sizes and finishes, with framed and matted options.",
});

onMounted(async () => {
  // Resolve the experiment hero and the collections in parallel; both are
  // best-effort and degrade to the static carousel / empty grid on failure.
  const [data, resolvedHero] = await Promise.all([
    collectionApi.getHomepageCollections().catch((): Collection[] => []),
    fetchHeroPayload(),
  ]);

  collections.value = [...data].sort(
    (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0),
  );

  hero.value = resolvedHero;
  heroImage.value = heroImageFor(resolvedHero);

  // Only the static carousel needs initializing; when an experiment hero is
  // shown the #homepageCarousel element isn't rendered (v-else), so this no-ops.
  const carouselElement = document.querySelector("#homepageCarousel");
  if (carouselElement) {
    // Respect prefers-reduced-motion: hold on the first slide instead of
    // auto-advancing for visitors who've asked for reduced motion.
    const reduceMotion =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    // ride/interval/pause OFF: Bootstrap handles only transitions + the
    // prev/next/indicator data-api; autoplay is the Vue timer below.
    carouselInstance = new carousel(carouselElement, {
      interval: false,
      ride: false,
      pause: false,
    }) as { dispose?: () => void; nextWhenVisible?: () => void };
    if (!reduceMotion) {
      carouselCanPause.value = true;
      startCarouselAutoplay();
    }
  }
});

onUnmounted(() => {
  // Tear down the autoplay timer + the carousel (releases the DOM it retains)
  // so returning to the homepage doesn't leak another instance or a live timer.
  stopCarouselAutoplay();
  carouselInstance?.dispose?.();
  carouselInstance = null;
});
</script>
<style scoped>
/** @format */

.promo-title {
  font-size: 0.9rem;
  font-weight: 500;
  margin-bottom: 0.2rem;
  line-height: 1.2;
  border-top: 1px solid #eaeaea;
}

.promo-text {
  font-size: 0.7rem;
  font-weight: 400;
  margin-bottom: 1rem;
  line-height: 1.2;
  padding-bottom: 0.6rem;
  border-bottom: 1px solid #eaeaea;
}

.carousel-image {
  /* Fill a fixed-height hero band and crop to cover, instead of width:100% +
     max-height (which distorts the ~3.69:1 banners on wide desktops). */
  width: 100%;
  height: 300px;
  object-fit: cover;
  object-position: center;
}

/* WCAG 2.2.2 pause/play control — a small, unobtrusive round button pinned to
   the top-right of the carousel, above the slides. */
.carousel-playpause {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  z-index: 3;
  width: 2rem;
  height: 2rem;
  padding: 0;
  border: 0;
  border-radius: 50%;
  line-height: 1;
  font-size: 0.85rem;
  color: #fff;
  background-color: rgba(0, 0, 0, 0.5);
}

.carousel-playpause:hover,
.carousel-playpause:focus-visible {
  background-color: rgba(0, 0, 0, 0.75);
}

.card-img-top {
  /* Same crop-don't-distort treatment for the collection banner cards
     (were forced to ~420x100 via the height attr -> squished). */
  width: 100%;
  height: 130px;
  object-fit: cover;
  object-position: center;
}

.card-img-overlay {
  display: flex;
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  background-color: rgba(0, 0, 0, 0.5);
  transition: background-color 0.3s;
}

.card:hover .card-img-overlay {
  background-color: rgba(0, 0, 0, 0.2);
}

.btn-text {
  color: #fff;
  padding: 5px 10px;
  text-align: center;
  transition: transform 0.3s;
}

.btn-text:hover {
  transform: translateY(-3px);
}

.carousel-link,
.card-link {
  color: inherit;
  text-decoration: none;
}

.collection-banner {
  top: 0;
  bottom: 0;
  position: absolute;
}

.collection-banner-bottom {
  align-content: end;
}

.collection-banner-top {
  align-content: start;
}

.collection-banner h1,
.collection-banner h2 {
  margin: 0;
  text-shadow: 1px 1px 5px rgba(255, 255, 255, 0.4);
  text-transform: uppercase;
  text-shadow: 2px 1px 2px black;
}

.collection-banner li {
  color: white;
  text-shadow: 2px 1px 1px black;
}

@media (max-width: 576px) {
  .collection-banner {
    padding: 8px;
  }

  .collection-banner h1 {
    font-size: 0.9rem;
    color: white;
  }

  .collection-banner h2 {
    font-size: 0.7rem;
    color: white;
  }

  .collection-banner ul {
    padding-left: 8px;
  }

  .collection-banner li {
    font-size: 0.5rem;
  }

  .collection-banner .btn {
    display: none;
  }
}

@media (min-width: 576px) {
  .collection-banner {
    padding: 10px;
  }

  .collection-banner h1 {
    color: white;
    font-size: 1.4em;
  }

  .collection-banner h2 {
    font-size: 1rem;
    color: white;
  }

  .collection-banner ul {
    padding-left: 10px;
  }

  .collection-banner li {
    font-size: 0.5rem;
  }

  .collection-banner .btn {
    display: none;
  }
}

@media (min-width: 768px) {
  .collection-banner {
    padding-left: 20px;
  }

  .collection-banner h1 {
    font-size: 1.4rem;
    font-weight: 500;
    line-height: 1;
  }

  .collection-banner h2 {
    font-size: 1rem;
    color: white;
  }

  .collection-banner li {
    font-size: 0.7rem;
  }

  .collection-banner .btn {
    display: inline;
    font-size: 0.8rem;
    margin-left: 0 !important;
  }
}

@media (min-width: 992px) {
  .collection-banner h1 {
    font-size: 2.4rem;
    font-weight: 700;
    line-height: 1.2;
  }

  .collection-banner h2 {
    font-size: 1.5rem;
    color: white;
  }

  .collection-banner li {
    font-size: 0.9rem;
  }

  .collection-banner .btn {
    display: inline;
  }
}

@media (min-width: 1200px) {
  .collection-banner {
    padding-left: 30px;
  }

  .collection-banner h1 {
    font-size: 2.4rem;
    font-weight: 700;
  }

  .collection-banner h2 {
    font-size: 1.8rem;
    color: white;
  }

  .collection-banner li {
    font-size: 1.1rem;
  }

  .collection-banner .btn {
    display: inline;
    font-size: 1rem;
  }
}

.background-overlay {
  background: linear-gradient(
    to right,
    rgba(0, 0, 0, 1),
    rgba(0, 0, 0, 0.75) 40%,
    rgba(0, 0, 0, 0) 80%
  );
}
</style>
